/**
 * Image Utilities
 * Functions for image compression and optimization
 */

import { env } from '../config/env'

/**
 * Compress an image file or data URL
 */
export async function compressImage(
  input: File | string,
  quality: number = env.imageQuality
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        // Calculate dimensions (max 1920x1080)
        const maxWidth = 1920
        const maxHeight = 1080
        let { width, height } = img

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = width * ratio
          height = height * ratio
        }

        canvas.width = width
        canvas.height = height

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)
        const compressed = canvas.toDataURL('image/jpeg', quality)

        resolve(compressed)
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => {
      reject(new Error('Failed to load image'))
    }

    // Load image
    if (typeof input === 'string') {
      img.src = input
    } else {
      const reader = new FileReader()
      reader.onload = (e) => {
        img.src = e.target?.result as string
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(input)
    }
  })
}

/**
 * Validate image file size
 */
export function validateImageSize(file: File): { valid: boolean; message?: string } {
  const maxSizeMB = env.maxImageSizeMB
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      message: `Image size must be less than ${maxSizeMB}MB`,
    }
  }

  return { valid: true }
}

/**
 * Validate image file type
 */
export function validateImageType(file: File): { valid: boolean; message?: string } {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      message: 'Only JPEG, PNG, and WebP images are allowed',
    }
  }

  return { valid: true }
}

/**
 * Get estimated compressed size in KB
 */
export function getEstimatedCompressedSize(dataUrl: string): number {
  // Remove data URL prefix
  const base64 = dataUrl.split(',')[1]
  if (!base64) return 0

  // Base64 to bytes (approximate)
  const bytes = (base64.length * 3) / 4
  return Math.round(bytes / 1024)
}
